import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { atividadesApi } from '../../api/atividades.js';

const DIAS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

function inicioDaSemanaAtual(): string {
  const hoje = new Date();
  const diaSemana = hoje.getUTCDay();
  const segunda = new Date(hoje);
  segunda.setUTCDate(hoje.getUTCDate() - ((diaSemana + 6) % 7));
  return segunda.toISOString().slice(0, 10);
}
function dataDoDia(inicio: string, offset: number): string {
  const data = new Date(inicio);
  data.setUTCDate(data.getUTCDate() + offset);
  return data.toISOString().slice(0, 10);
}

// SPEC-UX §4.6 — painel semanal fixável do Executor: grid atividade x dia,
// tab-tab-digita-enter. "Sem apontamento o Workload vira ficção" — é o
// atalho persistente para não deixar a semana sem preencher. Fixável (o
// usuário abre/fecha) em vez de sempre visível, para não competir por
// espaço nas telas densas (Programas/Portfólio).
export function PainelSemanalExecutor() {
  const [aberto, setAberto] = useState(false);
  const [recursoId, setRecursoId] = useState('');
  const queryClient = useQueryClient();
  const inicio = inicioDaSemanaAtual();

  const { data: celulas } = useQuery({
    queryKey: ['painel-semanal', recursoId, inicio],
    queryFn: () => atividadesApi.painelSemanal(recursoId, inicio, dataDoDia(inicio, 6)),
    enabled: aberto && recursoId.trim().length > 0,
  });

  const apontar = useMutation({
    mutationFn: ({ atividadeId, data, horas }: { atividadeId: string; data: string; horas: number }) =>
      atividadesApi.apontar(atividadeId, recursoId, horas, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['painel-semanal', recursoId, inicio] }),
  });

  function horasNaCelula(atividadeId: string, data: string): number {
    return celulas?.filter((c) => c.atividadeId === atividadeId && c.data === data).reduce((s, c) => s + c.horas, 0) ?? 0;
  }

  const atividadesUnicas = [...new Set(celulas?.map((c) => c.atividadeId) ?? [])];

  return (
    <div className="fixed bottom-0 right-4 z-40 w-80">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        className="rounded-t-lg border border-b-0 border-borda bg-superficie px-4 py-2 text-sm font-medium text-ink shadow"
      >
        Apontamento semanal {aberto ? '▾' : '▴'}
      </button>
      {aberto && (
        <div className="rounded-tr-lg border border-borda bg-superficie p-3 shadow-xl">
          <input
            placeholder="ID do recurso"
            value={recursoId}
            onChange={(e) => setRecursoId(e.target.value)}
            className="mb-2 w-full rounded border border-borda bg-base-100 px-2 py-1 text-xs text-ink"
          />
          {recursoId && (
            <>
              <table className="w-full text-xs">
                <thead>
                  <tr>
                    <th className="text-left text-grafite">Atividade</th>
                    {DIAS.map((d) => (
                      <th key={d} className="fonte-mono px-1 text-grafite">
                        {d}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {atividadesUnicas.map((atividadeId) => (
                    <tr key={atividadeId}>
                      <td className="fonte-mono max-w-[80px] truncate text-ink">{atividadeId}</td>
                      {DIAS.map((_, i) => (
                        <td key={i}>
                          <input
                            type="number"
                            min={0}
                            step={0.5}
                            defaultValue={horasNaCelula(atividadeId, dataDoDia(inicio, i)) || undefined}
                            onKeyDown={(e) => {
                              if (e.key !== 'Enter') return;
                              const horas = Number((e.target as HTMLInputElement).value);
                              if (horas > 0) apontar.mutate({ atividadeId, data: dataDoDia(inicio, i), horas });
                            }}
                            className="w-10 rounded border border-borda bg-base-100 px-1 py-0.5 text-center"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {atividadesUnicas.length === 0 && <p className="mt-2 text-xs text-grafite">Sem apontamentos nesta semana ainda.</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
